import { FC, KeyboardEvent, PointerEvent as ReactPointerEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const LEFT_PANEL_MIN_WIDTH = 210;
// The hotel list is never squeezed below this when the left panel is dragged wider.
const MAIN_MIN_WIDTH = 360;
// Same cap as `max-width: 70%` on .hotel-result-filter-left in hotel-modern.scss.
const MAX_PANEL_SHARE = 0.7;
const KEYBOARD_STEP = 20;
const STORAGE_KEY = 'hotel.result.leftPanelWidth';

const readStoredWidth = () => {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) && stored >= LEFT_PANEL_MIN_WIDTH ? stored : LEFT_PANEL_MIN_WIDTH;
  } catch {
    return LEFT_PANEL_MIN_WIDTH;
  }
};

/** Width of the left filter / AI assistant panel, remembered between visits. */
export const useLeftPanelWidth = () => {
  const [width, setWidth] = useState(readStoredWidth);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      // storage unavailable (private mode, blocked) - the width just isn't remembered
    }
  }, [width]);

  const reset = useCallback(() => setWidth(LEFT_PANEL_MIN_WIDTH), []);
  return { width, setWidth, reset };
};

interface ResultSplitterProps {
  width: number;
  onChange: (width: number) => void;
  onReset: () => void;
}

/**
 * Drag handle between the left panel and the hotel list. It is a sibling of the panel inside
 * `.hotel-result-data`, so the parent element is the container that bounds the panel width.
 */
const ResultSplitter: FC<ResultSplitterProps> = ({ width, onChange, onReset }) => {
  const { t } = useTranslation();

  // Stop a drag that is still running if the page unmounts (e.g. the user navigates away).
  useEffect(() => () => document.body.classList.remove('hotel-result-resizing'), []);

  const maxWidthFor = (container: HTMLElement | null) =>
    container
      ? Math.max(
          LEFT_PANEL_MIN_WIDTH,
          Math.min(container.clientWidth - MAIN_MIN_WIDTH, container.clientWidth * MAX_PANEL_SHARE),
        )
      : LEFT_PANEL_MIN_WIDTH;

  // The rendered width can differ from `width` (the CSS cap applies on a window smaller than when it was saved).
  const renderedWidth = (handle: HTMLElement) =>
    (handle.previousElementSibling as HTMLElement | null)?.getBoundingClientRect().width ?? width;

  const clamp = (value: number, container: HTMLElement | null) =>
    Math.round(Math.min(Math.max(value, LEFT_PANEL_MIN_WIDTH), maxWidthFor(container)));

  const isRtl = (container: HTMLElement | null) => !!container && getComputedStyle(container).direction === 'rtl';

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();

    const handle = e.currentTarget;
    const container = handle.parentElement;
    const direction = isRtl(container) ? -1 : 1;
    const startX = e.clientX;
    const startWidth = renderedWidth(handle);

    // Capture keeps pointermove coming to the handle even when the cursor is over the hotel map/iframes.
    handle.setPointerCapture(e.pointerId);
    document.body.classList.add('hotel-result-resizing');

    const onMove = (ev: PointerEvent) => onChange(clamp(startWidth + (ev.clientX - startX) * direction, container));
    const stop = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', stop);
      handle.removeEventListener('pointercancel', stop);
      document.body.classList.remove('hotel-result-resizing');
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', stop);
    handle.addEventListener('pointercancel', stop);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const container = e.currentTarget.parentElement;
    const direction = isRtl(container) ? -1 : 1;

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const step = (e.key === 'ArrowRight' ? KEYBOARD_STEP : -KEYBOARD_STEP) * direction;
      onChange(clamp(renderedWidth(e.currentTarget) + step, container));
    } else if (e.key === 'Home') {
      e.preventDefault();
      onReset();
    }
  };

  return (
    <div
      className="hotel-result-splitter"
      role="separator"
      aria-orientation="vertical"
      aria-label={t('hotel.resize_panel', 'Resize panel')}
      aria-valuenow={width}
      aria-valuemin={LEFT_PANEL_MIN_WIDTH}
      title={t('hotel.resize_panel_hint', 'Drag to resize, double-click to reset')}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onDoubleClick={onReset}
    />
  );
};

export default ResultSplitter;
